import axios from "axios";
import * as React from "react";
import { Link, Outlet, resolvePath, useLocation } from "react-router-dom";
import { ResourceDWR } from "../../../dto/ResourceDWR";
import { useSyndeticsConfig } from "../../SyndeticsConfig";
import {
  TitleTab,
  useLocationConfiguration,
} from "../../LocationConfiguration";
import { LS2PAC_CONSTANTS } from "src/main/resources/js/main/constants";
import { TLCEvents } from "../../../events/events";
import { RateAndReview } from "../../rate-and-review/RateAndReview";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLanguage } from "../../Language";
import MARCDialog from "../../MARCDialog";
import { TeachingBooksButton } from "./TeachingBooks";
import { RequestButton } from "../../place-hold/RequestButton";
import { DownloadButton } from "../resource/DownloadButton";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import { AddToListButton } from "../resource/AddToListButton";
import MoreMenu from "./MoreMenu";
import { ResultBookjacket } from "../search/ls/ResultBookJacket";
import { useDialog } from "@tlc/ui/dialog/useDialog";
import VirtualShelfDialog from "../account/VirtualShelfDialog";

interface HostSystemDiagImpl {
  methodSupported: boolean;
  validHostUser: boolean;
  validSessionUser: boolean;
  hostSystemFailure: boolean;
}

interface GetRealtimeHostRecordInformationReturn {
  hostSystemDiag: HostSystemDiagImpl;
  totalCheckouts: number;
  totalPendingRequests: number;
}

interface ITabDefinition {
  label: string;
  to: string;
}

async function getTotalCheckoutsAndPendingRequests({
  rtype,
  hostBibliographicId,
}: ResourceDWR) {
  if (rtype === 1) {
    try {
      const {
        data: { totalCheckouts, totalPendingRequests },
      } = await axios.get<GetRealtimeHostRecordInformationReturn>(
        `/hostSystem/getRealtimeHostRecordInformation/${hostBibliographicId}`,
      );
      return { totalCheckouts, totalPendingRequests };
    } catch (e) {
      return {
        totalCheckouts: null,
        totalPendingRequests: null,
      };
    }
  }
  return {
    totalCheckouts: null,
    totalPendingRequests: null,
  };
}

interface TitlePageProps {
  resource: ResourceDWR;
}
export const TitlePage = ({ resource }: TitlePageProps) => {
  const { locationConfiguration } = useLocationConfiguration();
  const { translate } = useLanguage();
  const location = useLocation();
  const {
    syndeticsConfig,
    hasMoreInfoTabEnrichments,
    hasRelatedTabEnrichments,
    hasReviewsTabEnrichments,
  } = useSyndeticsConfig();
  const theme = useTheme();

  const { open, openDialog, closeDialog } = useDialog();
  const fullSize = useMediaQuery(theme.breakpoints.up("md"));

  const showMARCRecordView =
    locationConfiguration?.pacSettings?.displaySettings?.showMarcRecordView;
  const showTotalCheckouts =
    locationConfiguration?.pacSettings?.displaySettings?.showTotalCheckouts;
  const showTotalHolds =
    locationConfiguration?.pacSettings?.displaySettings?.showTotalHolds;
  const displayVirtualShelf =
    locationConfiguration?.pacSettings?.displaySettings?.displayVirtualShelf;

  const [totalCheckouts, setTotalCheckouts] = React.useState<number>();
  const [totalPendingRequests, setTotalPendingRequests] =
    React.useState<number>();
  React.useEffect(() => {
    if (resource && (showTotalCheckouts || showTotalHolds)) {
      (async () => {
        const { totalCheckouts, totalPendingRequests } =
          await getTotalCheckoutsAndPendingRequests(resource);
        setTotalCheckouts(totalCheckouts);
        setTotalPendingRequests(totalPendingRequests);
      })();
    }
  }, [showTotalCheckouts, showTotalHolds, resource.id]);

  const tabs = React.useMemo(() => {
    if (!locationConfiguration) return;

    const { titleDetailsTabSetups, useNoveList, displaySettings } =
      locationConfiguration.pacSettings ?? {};
    const { showUserReviewsView } = displaySettings ?? {};

    const tabMap: Record<TitleTab, ITabDefinition | null> = {
      [TitleTab.Details]: {
        label: translate("details"),
        to: resolvePath(`../details`, location.pathname).pathname,
      },
      [TitleTab.AllCopies]: resource.downloadable
        ? null
        : {
            label: translate("allCopies"),
            to: resolvePath(`../copies`, location.pathname).pathname,
          },
      [TitleTab.Reviews]:
        showUserReviewsView || hasReviewsTabEnrichments
          ? {
              label: translate("reviews"),
              to: resolvePath(`../reviews`, location.pathname).pathname,
            }
          : null,
      [TitleTab.Related]:
        useNoveList || hasRelatedTabEnrichments
          ? {
              label: translate("related"),
              to: resolvePath(`../related`, location.pathname).pathname,
            }
          : null,
      [TitleTab.MoreInfo]: hasMoreInfoTabEnrichments
        ? {
            label: translate("lookInside"),
            to: resolvePath(`../more`, location.pathname).pathname,
          }
        : null,
    };

    return titleDetailsTabSetups
      .map((tab) => tabMap[tab.label])
      .filter((tab): tab is ITabDefinition => Boolean(tab));
  }, [
    hasMoreInfoTabEnrichments,
    hasRelatedTabEnrichments,
    hasReviewsTabEnrichments,
    locationConfiguration,
    translate,
    resource.id,
  ]);

  return (
    <>
      <Stack direction={fullSize ? "row" : "column"} sx={{ gap: 2, mt: 2 }}>
        <Box sx={{ width: fullSize ? "240px" : "auto" }}>
          <ResultBookjacket
            sx={{
              // minHeight: "200px",
              // maxWidth: "200px",
              margin: "0 auto 16px",
            }}
            resource={resource}
            height={200}
            width={132}
          />
          {/* <Bookjacket
            size={JacketSize.lg}
            sx={{
              minHeight: "200px",
              maxWidth: "200px",
              margin: "0 auto 16px",
            }}
            showFormat={false}
            resource={resource}
          ></Bookjacket> */}
        </Box>
        <Stack sx={{ flexGrow: 3, flexShrink: 1, minWidth: 0 }} gap={1}>
          <Typography variant="h4" component={"h2"}>
            {resource.shortTitle}
          </Typography>
          {!!resource.shortAuthor && (
            <Typography variant="h5" component={"h3"}>
              {translate("by")} {resource.shortAuthor}
            </Typography>
          )}
          <Stack
            direction={"row"}
            alignItems={"center"}
            sx={{ gap: "8px", flexWrap: "wrap" }}
          >
            {resource.format && (
              <Box>
                <Chip variant="outlined" label={resource.format} />
              </Box>
            )}

            <RateAndReview
              key={resource.id} //Trigger a Full Re-mount when our resource changes
              material={resource}
              ratingOnly
            ></RateAndReview>

            <Box flexGrow={1} sx={{ width: fullSize ? "100%" : "auto" }}>
              <TeachingBooksButton
                resource={resource}
                logoType={fullSize ? "horizontal" : "vertical"}
              ></TeachingBooksButton>
            </Box>
          </Stack>
          <Box flexGrow={1}></Box>
          <Stack
            direction={fullSize ? "row" : "row"}
            sx={{
              gap: 1,
              paddingTop: fullSize ? 2 : 1,
              paddingBottom: fullSize ? 2 : 1,
            }}
          >
            {/* <Stack direction={"row"} gap={2}> */}
            {!resource.downloadable && (
              <RequestButton
                sx={{ whiteSpace: "nowrap", flexGrow: fullSize ? 0 : 1 }}
                resource={resource}
              ></RequestButton>
            )}

            {resource.downloadable && <DownloadButton resource={resource} />}
            {/* </Stack> */}
            {fullSize && <AddToListButton resourceId={resource.id} />}
            {/* look for .virtualShelf event handler */}
            {fullSize && displayVirtualShelf && (
              <Button
                startIcon={<ViewWeekIcon />}
                onClick={openDialog}
                variant="outlined"
              >
                {translate("virtualShelfButtonText")}
              </Button>
            )}
            {!fullSize && <MoreMenu resource={resource} />}
          </Stack>
        </Stack>
      </Stack>
      {locationConfiguration && syndeticsConfig && (
        <Tabs
          value={location.pathname}
          orientation={"horizontal"}
          scrollButtons="auto"
          variant="scrollable"
          allowScrollButtonsMobile
          indicatorColor="secondary"
          textColor="secondary"
          sx={{ width: "100%" }}
        >
          {tabs.map(({ to, label }) => {
            return (
              <Tab
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: "row",
                  alignItems: "center",
                }}
                key={label}
                value={to}
                component={Link}
                to={to}
                label={label}
              ></Tab>
            );
          })}
        </Tabs>
      )}
      <Stack direction={fullSize ? "row-reverse" : "column-reverse"}>
        <Stack
          direction={"column"}
          sx={{
            alignItems: "center",
            flexGrow: 0,
            flexShrink: 0,
            width: fullSize ? "240px" : "auto",
            mx: fullSize ? 2 : 0,
            my: fullSize ? 0 : 2,
          }}
          gap={1}
        >
          {(showTotalHolds || showTotalCheckouts || showMARCRecordView) && (
            <Card sx={{ width: "100%" }}>
              <List dense={true} sx={{ width: "100%" }}>
                {showTotalHolds && (
                  <ListItem>
                    <ListItemText
                      primary={`${translate(
                        "totalRequests",
                      )}: ${totalPendingRequests}`}
                    />
                  </ListItem>
                )}
                {showTotalCheckouts && (
                  <ListItem>
                    <ListItemText
                      primary={`${translate(
                        "totalCheckouts",
                      )}: ${totalCheckouts}`}
                      secondary={translate("includingRenewals")}
                    />
                  </ListItem>
                )}
                {showMARCRecordView && (
                  <ListItem>
                    <MARCDialog resourceId={resource.id}></MARCDialog>
                  </ListItem>
                )}
              </List>
            </Card>
          )}
        </Stack>
        {/* minWidth needs to be set for flexShrink to work properly */}
        <Stack sx={{ flexGrow: 3, flexShrink: 1, minWidth: 0 }}>
          <Divider sx={{ mb: 2 }} />
          <Outlet context={resource}></Outlet>
        </Stack>
      </Stack>
      <VirtualShelfDialog
        resource={resource}
        open={open}
        onClose={closeDialog}
      />
    </>
  );
};
